import React from 'react'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {MBMemberRadio} from '../../../Atoms/MBMemberRadio'
import {ContractTerm, ErrorMessagesBaseForContractInput, generateContractTermLabelAndValueList} from '../../../../Props/ContractInputInfo'
import {MBMemberError} from '../../../../Common/MBMemberError'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {MBContentBodyItemTypography} from '../../../Atoms/Typographies'
import {PIMCard} from '../../../Atoms/Cards/PIMCard'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'
import {PIMWarningMessages} from '../../../Atoms/Labels/PIMWarningMessages'
import {PaymentInformation} from '../../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'

type ChangeNextContractTermContentProps = {
  paymentInformationList: PaymentInformation[]
  defaultValue: ContractTerm
  value: ContractTerm
  onChange: (changeSelectedTerm: ContractTerm) => void
  errorMessage: ErrorMessagesBaseForContractInput['paymentInputTermErrors']
}

export const ChangeNextContractTermContent: React.VFC<ChangeNextContractTermContentProps> =
  ({
     paymentInformationList,
     defaultValue,
     value,
     onChange,
     errorMessage,
   }) => {
    const isFreeBucket = paymentInformationList.some(payInfo => payInfo.isFreeBucket)

    const paymentInformationListExceptCurrentTerm =
      paymentInformationList.find(value => value.term !== defaultValue)
    if (isFreeBucket || !paymentInformationListExceptCurrentTerm) {
      return <></>
    }

    return (
      <PIMCard
        headerProps={{title: '次回契約単位の変更', require: true}}
        testId={'block-change-contract-term-unit'}
      >
        {errorMessage.length > 0 && (
          <Spacer mb={'18px'} data-testid={'block-select-contract-term-warning-message'}>
            <PIMWarningMessages messages={errorMessage}/>
          </Spacer>
        )}
        <ContractUnitRadioButtons
          paymentInformationList={paymentInformationList}
          defaultValue={defaultValue}
          value={value}
          hasError={errorMessage.length > 0}
          onChange={(changeSelectedTerm: ContractTerm): void => {
            onChange(changeSelectedTerm)
          }}
        />
      </PIMCard>
    )
  }

type NextTermPaymentMethodPropsAddOnChange = {
  paymentInformationList: PaymentInformation[],
  defaultValue: ContractTerm
  value: ContractTerm
  hasError: boolean
  onChange: (changeSelectedTerm: ContractTerm) => void
}

const ContractUnitRadioButtons: React.VFC<NextTermPaymentMethodPropsAddOnChange> = (
  {
    paymentInformationList,
    defaultValue,
    value,
    hasError,
    onChange,
  },
) => {
  const contractTermLabelAndValueList =
    generateContractTermLabelAndValueList(
      paymentInformationList,
    )

  const foundCurrentContractTermLabelAndValue = contractTermLabelAndValueList
    .find(value => value.value === defaultValue)
  if (!foundCurrentContractTermLabelAndValue) {
    throw new MBMemberError('SystemError')
  }

  return (
    <PIMStack spacing={'16px'} divider={<MBContentBodyDivider/>} showEndDivider>
      <PIMStack spacing={1.5}>
        <MBMemberRadio
          labelName={'契約単位を変更しない'}
          value={foundCurrentContractTermLabelAndValue.value}
          isBoldLabel={true}
          fontRemSize={1.2}
          hasError={hasError}
          onChange={(): void => {
            onChange(foundCurrentContractTermLabelAndValue.value)
          }}
          checked={value === foundCurrentContractTermLabelAndValue.value}
        />
        <Spacer ml={'28px'}>
          <MBContentBodyItemTypography>
            {foundCurrentContractTermLabelAndValue.label}
          </MBContentBodyItemTypography>
        </Spacer>
      </PIMStack>
      {contractTermLabelAndValueList.map((labelAndValue, index) => (
        defaultValue !== labelAndValue.value && (
          <PIMStack spacing={1.5} key={index}>
            <MBMemberRadio
              labelName={'契約単位を変更する'}
              value={labelAndValue.value}
              isBoldLabel={true}
              fontRemSize={1.2}
              hasError={hasError}
              onChange={(): void => {
                onChange(labelAndValue.value)
              }}
              checked={value === labelAndValue.value}
            />
            <Spacer ml={'28px'}>
              <MBContentBodyItemTypography>
                {labelAndValue.label}
              </MBContentBodyItemTypography>
            </Spacer>
          </PIMStack>
        )
      ))}
    </PIMStack>
  )
}
